import { useState } from "react";
import { Download, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { composeSnapshot, snapshotFilename } from "./snapshot";

const btn = "h-9 inline-flex items-center gap-2 rounded-full bg-[#0D0D11]/80 backdrop-blur-md border border-[#D4AF37]/35 px-4 font-mono-j text-[10px] uppercase tracking-[0.2em] text-[#F5F3EF] hover:border-[#E5C158] hover:text-[#E5C158] transition-colors disabled:opacity-50";

/** Save / share buttons for the 3D preview. captureRef.current() returns the freshly rendered WebGL canvas. */
export const SnapshotButtons = ({ captureRef, pair }) => {
  const [busy, setBusy] = useState(null);
  const canShare = typeof navigator !== "undefined" && !!navigator.canShare;

  const grab = async () => {
    const blob = await composeSnapshot(captureRef.current(), pair);
    return new File([blob], snapshotFilename(pair), { type: "image/png" });
  };

  const download = async () => {
    setBusy("save");
    try {
      const file = await grab();
      const url = URL.createObjectURL(file);
      Object.assign(document.createElement("a"), { href: url, download: file.name }).click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Photo saved", { description: "Attach it to a quote request or share it with your client." });
    } catch {
      toast.error("Couldn't capture the kitchen view");
    } finally { setBusy(null); }
  };

  const share = async () => {
    setBusy("share");
    try {
      const file = await grab();
      const data = { files: [file], title: "Sterling Joinery pairing", text: `${pair.board?.name ?? ""} + ${pair.stone?.name ?? ""}`.trim() };
      if (navigator.canShare(data)) await navigator.share(data);
      else await download();
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Sharing isn't available here — use Save photo instead");
    } finally { setBusy(null); }
  };

  return (
    <div className="absolute right-3 top-3 flex gap-2" data-testid="pair-snapshot-actions">
      <button onClick={download} disabled={!!busy} className={btn} data-testid="pair-snapshot-save-btn">
        {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Save photo
      </button>
      {canShare && (
        <button onClick={share} disabled={!!busy} className={btn} data-testid="pair-snapshot-share-btn">
          {busy === "share" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />} Share
        </button>
      )}
    </div>
  );
};
